import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Clock3, Loader2, LogIn, LogOut, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAttendanceClock } from '../hooks/useAttendanceClock';
import { useAttendanceLocationAccess } from '../hooks/useAttendanceLocationAccess';
import { useToast } from '../hooks/useToast';

type ClockConfirmation = {
  action: 'in' | 'out';
  description: string;
  left: number;
  stamp: number;
  title: string;
  top: number;
};

export default function SidebarAttendanceClock() {
  const { user } = useAuth();

  if (!user) return null;

  return <SidebarAttendanceClockControl user={user} />;
}

function SidebarAttendanceClockControl({ user }: { user: any }) {
  const { toast } = useToast();
  const clockState = useAttendanceClock(user);
  const location = useAttendanceLocationAccess(
    clockState.requiresLocation && !clockState.loading,
  );
  const [confirmation, setConfirmation] = useState<ClockConfirmation | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!confirmation) return undefined;

    const timeout = window.setTimeout(() => setConfirmation(null), 3600);
    return () => window.clearTimeout(timeout);
  }, [confirmation]);

  const action = clockState.nextAction;
  const isBusy = clockState.busy !== null;
  const locationBlocked =
    clockState.requiresLocation && location.status !== 'granted';
  const isCheckingLocation = locationBlocked && location.status === 'checking';
  const isWaiting = clockState.loading || isBusy || isCheckingLocation;
  const isDisabled = isWaiting || (!locationBlocked && action === null);
  const label = locationBlocked
    ? isCheckingLocation
      ? 'Checking location'
      : 'Allow Location'
    : clockState.loading
    ? 'Checking attendance'
    : isBusy
    ? clockState.busy === 'in'
      ? 'Clocking in'
      : 'Clocking out'
    : action === 'out'
      ? 'Clock Out'
      : action === 'in'
        ? 'Clock In'
        : 'Attendance done for today';
  const tooltipText = locationBlocked
    ? location.error || 'Allow location access to use attendance.'
    : clockState.error || label;
  const Icon = isWaiting
    ? Loader2
    : locationBlocked
      ? MapPin
      : clockState.active
        ? LogOut
        : action === 'in'
          ? LogIn
          : Clock3;
  const buttonClassName = [
    'gt-sidebar-clock-btn',
    action === 'in' && 'is-clock-in',
    action === 'out' && 'is-clock-out',
    clockState.active && 'is-active',
    clockState.doneForToday && 'is-done',
    isDisabled && 'is-disabled',
  ].filter(Boolean).join(' ');

  const handleClick = async () => {
    if (isDisabled) return;
    if (locationBlocked) {
      await location.requestAccess();
      return;
    }
    if (!action) return;
    setConfirmation(null);
    try {
      await clockState.clock(action);
      const rect = buttonRef.current?.getBoundingClientRect();
      setConfirmation({
        action,
        description: action === 'in'
          ? "You're marked present for today."
          : 'Your attendance is saved for today.',
        left: rect ? rect.right + 12 : 12,
        stamp: Date.now(),
        title: action === 'in' ? 'Clocked in successfully' : 'Clocked out successfully',
        top: rect ? rect.top + rect.height / 2 : 24,
      });
      toast({
        title: action === 'in' ? 'Clocked in' : 'Clocked out',
        description: action === 'in'
          ? "You're marked present for today."
          : 'Have a good rest of your day.',
      });
    } catch (err: any) {
      toast({
        title: action === 'in' ? "Couldn't clock in" : "Couldn't clock out",
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="gt-sidebar-clock-slot">
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        onClick={handleClick}
        aria-disabled={isDisabled}
        aria-describedby="gt-sidebar-clock-tooltip"
        aria-label={label}
        aria-pressed={clockState.active}
        title={tooltipText}
      >
        <Icon className={`gt-sidebar-clock-btn__icon${isWaiting ? ' is-spinning' : ''}`} />
        {clockState.active && <span className="gt-sidebar-clock-btn__dot" aria-hidden />}
      </button>
      <span id="gt-sidebar-clock-tooltip" className="gt-sidebar-clock-tooltip" role="tooltip">
        {tooltipText}
      </span>
      {confirmation && createPortal(
        <div
          key={confirmation.stamp}
          className={`gt-sidebar-clock-confirmation is-${confirmation.action}`}
          role="status"
          aria-live="polite"
          style={{ left: confirmation.left, top: confirmation.top }}
        >
          <CheckCircle2 className="gt-sidebar-clock-confirmation__icon" aria-hidden />
          <span className="gt-sidebar-clock-confirmation__copy">
            <strong>{confirmation.title}</strong>
            <small>{confirmation.description}</small>
          </span>
        </div>,
        document.body,
      )}
    </div>
  );
}
